import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../../config/firebase.ts';
import PlayerName from '../Common/PlayerName.tsx';
import './Christmas.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';

interface AdventDay {
  day: number;
  theme: string;
  emoji: string;
  category: string;
  unlocked: boolean;
  completed: boolean;
  score: number;
  completedAt: string | null;
  missed: boolean;
  isToday: boolean;
}

interface ChristmasEffect {
  id: string;
  name: string;
  cssClass: string;
  description: string;
  emoji: string;
  unlocked: boolean;
  canUnlock: boolean;
}

interface CalendarData {
  calendar: AdventDay[];
  currentDay: number;
  locked: boolean;
  totalCompleted: number;
  totalDays: number;
  hasCompletedAll: boolean;
  effect: ChristmasEffect;
  progress: number;
}

const ChristmasCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [showEffectUnlocked, setShowEffectUnlocked] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (!u) navigate('/login');
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    async function fetchCalendar() {
      if (!user) return;
      
      try {
        const token = await user.getIdToken();
        const res = await fetch(`${API_URL}/api/christmas/calendar`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        
        const data = await res.json();
        
        // Vérifier que la structure est correcte
        if (!data || !data.calendar) {
          console.error('Structure de données incorrecte:', data);
          return;
        }
        
        setCalendarData(data);
      } catch (error) {
        console.error('Erreur lors du chargement du calendrier:', error);
      }
    }
    fetchCalendar();
  }, [user]);

  const handleDayClick = (day: AdventDay) => {
    if (!day.unlocked || day.completed || day.missed) return;
    setSelectedDay(day.day);
    navigate(`/christmas/day/${day.day}`);
  };

  const handleLogoutAndRedirect = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const getDayClassName = (day: AdventDay): string => {
    let className = 'advent-day';
    
    if (day.completed) className += ' completed';
    else if (day.missed) className += ' missed';
    else if (day.isToday) className += ' today pulse';
    else if (day.unlocked) className += ' unlocked';
    else className += ' locked';
    
    return className;
  };

  if (loading) return <div className="christmas-loading">🎄 Chargement...</div>;
  
  // Utilisateur non connecté ou anonyme
  if (!user || user.isAnonymous) {
    return (
      <div className="christmas-container">
        {/* Effet de neige */}
        <div className="snowfall">
          {[...Array(50)].map((_, i) => (
            <div key={i} className="snowflake" style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${5 + Math.random() * 10}s`
            }}>❄</div>
          ))}
        </div>

        <div className="christmas-auth-required">
          <div className="auth-card">
            <div className="auth-decoration">
              <span className="deco-item">🎄</span>
              <span className="deco-item">🎅</span>
              <span className="deco-item">🎁</span>
            </div>
            
            <h1 className="auth-title">Calendrier de l'Avent 2025</h1>
            
            <div className="auth-icon">🔒</div>
            
            <p className="auth-message">
              Créez un compte ou connectez-vous pour accéder au 
              <strong> Calendrier de l'Avent</strong> et débloquer 
              l'effet exclusif <PlayerName userId="demo" userName="Flocon de Neige" hasChristmasEffect={true} /> 
              sur votre pseudo !
            </p>
            
            <div className="auth-features">
              <div className="feature">
                <span className="feature-icon">📅</span>
                <span>24 quiz festifs</span>
              </div>
              <div className="feature">
                <span className="feature-icon">🏆</span>
                <span>Classement spécial</span>
              </div>
              <div className="feature">
                <span className="feature-icon">✨</span>
                <span>Effet exclusif</span>
              </div>
            </div>
            
            <div className="auth-buttons">
              <button 
                onClick={handleLogoutAndRedirect} 
                className="auth-btn primary"
              >
                🎅 Se connecter
              </button>
              <button 
                onClick={handleLogoutAndRedirect} 
                className="auth-btn secondary"
              >
                🎁 Créer un compte
              </button>
            </div>
            
            <button 
              onClick={() => navigate('/')} 
              className="auth-back-btn"
            >
              ← Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="christmas-container">
      {/* Effet de neige */}
      <div className="snowfall">
        {[...Array(50)].map((_, i) => (
          <div key={i} className="snowflake" style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 10}s`,
            animationDuration: `${5 + Math.random() * 10}s`
          }}>❄</div>
        ))}
      </div>

      {/* Header */}
      <header className="xmas-lb-header">
        <div className="xmas-lb-title-wrapper">
          <div className="xmas-lb-ornament left">🎄</div>
          <h1 className="xmas-lb-title">
            <span className="title-line">Calendrier</span>
            <span className="title-line accent">de l'Avent 2025</span>
          </h1>
          <div className="xmas-lb-ornament right">🎄</div>
        </div>
        
        {(() => {
          const now = new Date();
          const christmas = new Date(now.getFullYear(), 11, 25);
          const daysUntilChristmas = Math.max(0, Math.ceil((christmas.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
          return daysUntilChristmas > 0 && (
            <div className="xmas-lb-countdown">
              <span className="countdown-icon">🎅</span>
              <span className="countdown-text">
                Plus que <strong>{daysUntilChristmas}</strong> jour{daysUntilChristmas > 1 ? 's' : ''} avant Noël !
              </span>
            </div>
          );
        })()}

        <nav className="xmas-lb-nav">
          <button onClick={() => navigate('/christmas/leaderboard')} className="xmas-lb-btn primary">
            <span className="btn-icon">🏆</span>
            <span>Classement</span>
          </button>
          <button onClick={() => navigate('/')} className="xmas-lb-btn">
            <span className="btn-icon">🏠</span>
            <span>Accueil</span>
          </button>
        </nav>
      </header>

      {/* Progression avec guirlande lumineuse */}
      <div className="christmas-progress">
        <div className="progress-header">
          <div className="progress-title">
            <span className="progress-icon">✨</span>
            <span className="progress-text">Illuminons le chemin vers Noël</span>
            <span className="progress-icon">✨</span>
          </div>
          <div className="progress-stats">
            <span className="stat-completed">{calendarData?.totalCompleted || 0}/24</span>
            <span className="stat-label">jours complétés</span>
          </div>
        </div>
        
        {/* Guirlande de Noël */}
        <div className="christmas-lights-container">
          <div className="lights-wire"></div>
          <div className="christmas-lights">
            {[...Array(24)].map((_, i) => {
              const completed = (calendarData?.totalCompleted || 0) > i;
              const colors = ['#ff6b6b', '#4ecdc4', '#ffe66d', '#a8e6cf', '#ff8787', '#95e1d3'];
              const color = colors[i % colors.length];
              
              return (
                <div 
                  key={i} 
                  className={`light-bulb ${completed ? 'lit' : 'dark'}`}
                  style={{
                    '--bulb-color': color,
                    animationDelay: `${i * 0.1}s`
                  } as React.CSSProperties}
                >
                  <div className="bulb-glow"></div>
                  <div className="bulb-base"></div>
                  {completed && (
                    <div className="bulb-shine"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Effet spécial */}
      <div className={`christmas-effect-banner ${calendarData?.effect?.unlocked ? 'unlocked' : ''}`}>
        {calendarData?.effect?.unlocked ? (
          <>
            <span className="effect-emoji">❄️</span>
            <span className="effect-text">
              Effet <PlayerName userId="demo" userName="Flocon de Noël" hasChristmasEffect={true} /> débloqué ! Visible sur votre pseudo partout !
            </span>
            <span className="effect-emoji">❄️</span>
          </>
        ) : (
          <>
            <span className="effect-emoji">🔒</span>
            <span className="effect-text">
              Complétez les 24 cases pour débloquer l'effet <PlayerName userId="demo" userName="Flocon de Noël" hasChristmasEffect={true} /> sur votre pseudo !
            </span>
            <span className="effect-emoji">🎁</span>
          </>
        )}
      </div>

      {/* Calendrier verrouillé */}
      {calendarData?.locked && (
        <div className="calendar-locked-banner">
          <span>🔒</span>
          <span>Le Calendrier de l'Avent 2025 est terminé !</span>
          {calendarData?.hasCompletedAll ? (
            <span>🎉 Félicitations, vous avez tout complété !</span>
          ) : (
            <span>😢 Vous avez manqué des cases...</span>
          )}
        </div>
      )}

      {/* Grille du calendrier */}
      <div className="advent-grid">
        {!calendarData ? (
          <div className="calendar-loading">
            <div className="loading-snowflake">❄️</div>
            <p>Chargement du calendrier...</p>
          </div>
        ) : (
          calendarData.calendar?.map((day) => (
            <div
              key={day.day}
              className={getDayClassName(day)}
              onClick={() => handleDayClick(day)}
            >
              <div className="day-number">{day.day}</div>
              <div className="day-emoji">{day.emoji}</div>
              {day.completed && (
                <div className="day-completed-badge">✅</div>
              )}
              {day.missed && (
                <div className="day-missed-badge">❌</div>
              )}
              {day.isToday && !day.completed && (
                <div className="day-today-badge">AUJOURD'HUI</div>
              )}
              {(day.completed || day.isToday || day.missed) ? (
                <div className="day-theme">{day.theme}</div>
              ) : (
                <div className="day-theme day-theme-hidden">???</div>
              )}
              {day.completed && day.score > 0 && (
                <div className="day-score">{day.score} pts</div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Bouton Classement */}
      <div className="christmas-actions">
        <button 
          onClick={() => navigate('/christmas/leaderboard')}
          className="christmas-leaderboard-btn"
        >
          🏆 Classement de l'Avent
        </button>
      </div>

      {/* Modal effet débloqué */}
      {showEffectUnlocked && (
        <div className="effect-modal-overlay" onClick={() => setShowEffectUnlocked(false)}>
          <div className="effect-modal" onClick={e => e.stopPropagation()}>
            <div className="effect-modal-content">
              <div className="effect-celebration">🎉❄️🎄❄️🎉</div>
              <h2>Félicitations !</h2>
              <p>Vous avez débloqué l'effet</p>
              <div className="effect-name-display">
                <span className="christmas-snowflake-effect">❄️ Flocon de Noël ❄️</span>
              </div>
              <p>Cet effet sera visible sur votre pseudo dans tous les jeux !</p>
              <button onClick={() => setShowEffectUnlocked(false)}>
                Génial ! 🎅
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChristmasCalendar;
